import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

// Connects to data-controller="admin-bar-chart"
export default class extends Controller {
  static targets = ["chart", "periodSelect", "legendItem"]
  static values = {
    series: Array,
    categories: Array,
    colors: Array,
    height: { type: Number, default: 335 },
    stacked: { type: Boolean, default: false },
    columnWidth: { type: String, default: "25%" },
    showLegend: { type: Boolean, default: true },
    showGrid: { type: Boolean, default: false },
    borderRadius: { type: Number, default: 0 }
  }

  connect() {
    this.initChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  initChart() {
    const options = this.buildOptions()
    this.chart = new ApexCharts(this.chartTarget, options)
    this.chart.render()
  }

  buildOptions() {
    const options = {
      series: this.seriesValue,
      colors: this.colorsValue,
      chart: {
        fontFamily: "Satoshi, sans-serif",
        type: "bar",
        height: this.heightValue,
        stacked: this.stackedValue,
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        }
      },
      responsive: [
        {
          breakpoint: 1536,
          options: {
            plotOptions: {
              bar: {
                borderRadius: 0,
                columnWidth: this.columnWidthValue
              }
            }
          }
        }
      ],
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: this.borderRadiusValue,
          columnWidth: this.columnWidthValue,
          borderRadiusApplication: "end",
          borderRadiusWhenStacked: "last"
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        show: true,
        width: 4,
        colors: ["transparent"]
      },
      xaxis: {
        categories: this.categoriesValue,
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        },
        labels: {
          style: {
            colors: "#64748B",
            fontSize: "12px",
            fontFamily: "Satoshi, sans-serif"
          }
        }
      },
      legend: {
        show: false
      },
      yaxis: {
        title: false,
        labels: {
          style: {
            colors: "#64748B",
            fontSize: "12px",
            fontFamily: "Satoshi, sans-serif"
          }
        }
      },
      grid: {
        strokeDashArray: this.showGridValue ? 7 : 0,
        yaxis: {
          lines: {
            show: this.showGridValue
          }
        }
      },
      fill: {
        opacity: 1
      },
      tooltip: {
        x: {
          show: false
        },
        y: {
          formatter: function (val) {
            return val
          }
        }
      }
    }

    return options
  }

  selectPeriod(event) {
    const select = event.currentTarget
    const period = select.value

    // Dispatch custom event for period change
    this.dispatch("periodChange", { detail: { period } })
  }

  // Public method to update chart data
  updateSeries(newSeries) {
    if (this.chart) {
      this.chart.updateSeries(newSeries)
    }
  }

  // Public method to update categories
  updateCategories(newCategories) {
    if (this.chart) {
      this.chart.updateOptions({
        xaxis: {
          categories: newCategories
        }
      })
    }
  }

  // Public method to update both series and categories
  updateData(newSeries, newCategories) {
    if (this.chart) {
      this.chart.updateOptions({
        series: newSeries,
        xaxis: {
          categories: newCategories
        }
      })
    }
  }

  // Public method to toggle series visibility
  toggleSeries(seriesName) {
    if (this.chart) {
      this.chart.toggleSeries(seriesName)
    }
  }

  // Toggle legend item visibility (called from click on custom legend)
  toggleLegendItem(event) {
    const button = event.currentTarget
    const seriesName = button.dataset.seriesName
    
    if (this.chart) {
      this.chart.toggleSeries(seriesName)
      
      // Toggle visual state of the legend item
      button.classList.toggle('opacity-50')
    }
  }

  // Public method to update chart options
  updateOptions(newOptions) {
    if (this.chart) {
      this.chart.updateOptions(newOptions)
    }
  }
}
